import React, { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import axios from 'axios';
import { Award, Plus, Clock, Zap } from 'lucide-react';
import { Button } from '@/components/ui/button';
import DashboardLayout from '@/components/DashboardLayout';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SUBJECTS = [
  { value: 'Telugu', label: 'తెలుగు (Telugu)', icon: '📚' },
  { value: 'Hindi', label: 'हिंदी (Hindi)', icon: '📖' },
  { value: 'English', label: 'English', icon: '📝' },
  { value: 'Mathematics', label: 'Mathematics', icon: '🔢' },
  { value: 'Social', label: 'Social Studies', icon: '🌍' },
  { value: 'Physics', label: 'Physics', icon: '⚛️' },
  { value: 'Biology', label: 'Biology', icon: '🧬' }
];

const MARKS_TYPES = ['1M', '2M', '4M', '8M', 'Mixed'];
const DIFFICULTY_LEVELS = ['Easy', 'Medium', 'Hard', 'Mixed'];

export default function PracticeTests() {
  const { getAuthHeaders } = useAuth();
  const [formData, setFormData] = useState({
    subject: 'Mathematics',
    chapter: '',
    marks_type: 'Mixed',
    difficulty: 'Mixed',
    num_questions: 20
  });
  const [chapters, setChapters] = useState([]);
  const [test, setTest] = useState(null);
  const [loading, setLoading] = useState(false);
  const [timedMode, setTimedMode] = useState(false);
  const [timeLeft, setTimeLeft] = useState(0);

  useEffect(() => {
    fetchChapters(formData.subject);
  }, [formData.subject]);

  useEffect(() => {
    let timer;
    if (timedMode && timeLeft > 0) {
      timer = setInterval(() => {
        setTimeLeft(prev => {
          if (prev <= 1) {
            setTimedMode(false);
            toast.info('Time is up!');
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => clearInterval(timer);
  }, [timedMode, timeLeft]);

  const fetchChapters = async (subject) => {
    try {
      const response = await axios.get(`${API}/subjects/chapters?subject=${subject}`, {
        headers: getAuthHeaders()
      });
      setChapters(response.data.chapters);
      setFormData(prev => ({ ...prev, chapter: '' }));
    } catch (error) {
      console.error('Failed to fetch chapters');
    }
  };

  const handleGenerate = async (e) => {
    e.preventDefault();
    setLoading(true);
    setTest(null);
    setTimedMode(false);

    try {
      const payload = {
        subject: formData.subject,
        chapter: formData.chapter || null,
        marks_type: formData.marks_type === 'Mixed' ? null : formData.marks_type,
        difficulty: formData.difficulty === 'Mixed' ? null : formData.difficulty,
        num_questions: formData.num_questions
      };

      const response = await axios.post(
        `${API}/tests/generate`,
        payload,
        { headers: getAuthHeaders() }
      );
      setTest(response.data);
      toast.success('Practice test generated!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to generate test');
    } finally {
      setLoading(false);
    }
  };

  const startTimedTest = () => {
    if (!test) return;
    // Calculate time: 1.5 minutes per question
    const totalTime = test.questions.length * 90;
    setTimeLeft(totalTime);
    setTimedMode(true);
    toast.success('Timed test started!');
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <DashboardLayout>
      <div className="space-y-8 fade-in">
        <div data-testid="practice-tests-header" className="bg-white rounded-lg border border-slate-200 p-8">
          <h1 className="text-3xl font-bold text-slate-900 mb-2">Practice Tests</h1>
          <p className="text-slate-600">Generate unlimited practice tests with chapter-wise focus and difficulty levels</p>
        </div>

        <div data-testid="test-generator-form" className="bg-white rounded-lg border border-slate-200 p-8">
          <form onSubmit={handleGenerate} className="space-y-6">
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <label htmlFor="subject" className="block text-sm font-medium text-slate-700 mb-2">
                  Subject
                </label>
                <select
                  id="subject"
                  data-testid="test-subject-select"
                  value={formData.subject}
                  onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                  className="w-full px-4 py-3 bg-slate-50 border border-transparent rounded-md focus:bg-white focus:border-blue-900 focus:ring-0"
                >
                  {SUBJECTS.map((subject) => (
                    <option key={subject.value} value={subject.value}>
                      {subject.icon} {subject.label}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label htmlFor="chapter" className="block text-sm font-medium text-slate-700 mb-2">
                  Chapter/Topic (Optional)
                </label>
                <select
                  id="chapter"
                  data-testid="test-chapter-select"
                  value={formData.chapter}
                  onChange={(e) => setFormData({ ...formData, chapter: e.target.value })}
                  className="w-full px-4 py-3 bg-slate-50 border border-transparent rounded-md focus:bg-white focus:border-blue-900 focus:ring-0"
                >
                  <option value="">All Chapters</option>
                  {chapters.map((chapter, idx) => (
                    <option key={idx} value={chapter}>{chapter}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="grid md:grid-cols-3 gap-6">
              <div>
                <label htmlFor="marks_type" className="block text-sm font-medium text-slate-700 mb-2">
                  Question Type
                </label>
                <select
                  id="marks_type"
                  data-testid="test-marks-select"
                  value={formData.marks_type}
                  onChange={(e) => setFormData({ ...formData, marks_type: e.target.value })}
                  className="w-full px-4 py-3 bg-slate-50 border border-transparent rounded-md focus:bg-white focus:border-blue-900 focus:ring-0"
                >
                  {MARKS_TYPES.map((type) => (
                    <option key={type} value={type}>{type}</option>
                  ))}
                </select>
              </div>

              <div>
                <label htmlFor="difficulty" className="block text-sm font-medium text-slate-700 mb-2">
                  <Zap className="w-4 h-4 inline mr-1" />
                  Difficulty
                </label>
                <select
                  id="difficulty"
                  data-testid="test-difficulty-select"
                  value={formData.difficulty}
                  onChange={(e) => setFormData({ ...formData, difficulty: e.target.value })}
                  className="w-full px-4 py-3 bg-slate-50 border border-transparent rounded-md focus:bg-white focus:border-blue-900 focus:ring-0"
                >
                  {DIFFICULTY_LEVELS.map((level) => (
                    <option key={level} value={level}>{level}</option>
                  ))}
                </select>
              </div>

              <div>
                <label htmlFor="num_questions" className="block text-sm font-medium text-slate-700 mb-2">
                  Number of Questions
                </label>
                <input
                  id="num_questions"
                  data-testid="test-num-input"
                  type="number"
                  min="5"
                  max="60"
                  value={formData.num_questions}
                  onChange={(e) => setFormData({ ...formData, num_questions: parseInt(e.target.value) })}
                  className="w-full px-4 py-3 bg-slate-50 border border-transparent rounded-md focus:bg-white focus:border-blue-900 focus:ring-0"
                />
                <p className="text-xs text-slate-500 mt-1">Max: 60 questions (Recommended: 20-40 for faster generation)</p>
              </div>
            </div>

            <Button
              data-testid="generate-test-btn"
              type="submit"
              disabled={loading}
              className="w-full bg-blue-900 hover:bg-blue-800 text-white py-6 text-lg btn-primary"
            >
              <Plus className="w-5 h-5 mr-2" />
              {loading ? 'Generating Test...' : 'Generate Practice Test'}
            </Button>
          </form>
        </div>

        {test && (
          <div data-testid="test-display" className="bg-white rounded-lg border border-slate-200 p-8 fade-in">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-2xl font-bold text-slate-900">Practice Test - {test.subject}</h2>
                {test.chapter && (
                  <p className="text-slate-600 mt-1">{test.chapter}</p>
                )}
              </div>
              <div className="flex items-center space-x-3">
                {timedMode && (
                  <div className={`flex items-center space-x-2 px-4 py-2 rounded-md ${
                    timeLeft < 300 ? 'bg-red-50 text-red-700' : 'bg-blue-50 text-blue-700'
                  }`}>
                    <Clock className="w-5 h-5" />
                    <span className="font-bold text-lg">{formatTime(timeLeft)}</span>
                  </div>
                )}
                {!timedMode && (
                  <Button
                    onClick={startTimedTest}
                    variant="outline"
                    className="border-blue-900 text-blue-900"
                  >
                    <Clock className="w-4 h-4 mr-2" />
                    Start Timed Test
                  </Button>
                )}
                <span className="marks-badge bg-green-50 text-green-700 px-4 py-2 rounded-full">
                  {test.questions.length} Questions
                </span>
              </div>
            </div>

            <div className="space-y-6">
              {test.questions.map((q, index) => (
                <div key={index} className="p-6 bg-slate-50 rounded-md border border-slate-200">
                  <div className="flex items-start justify-between mb-3">
                    <span className="text-sm font-bold text-blue-900">Q{index + 1}.</span>
                    <span className="marks-badge bg-blue-50 text-blue-700 px-2 py-1 rounded text-xs">
                      {q.marks}M
                    </span>
                  </div>
                  <p className="text-slate-900 leading-relaxed">{q.question}</p>
                </div>
              ))}
            </div>

            {timedMode && (
              <div className="mt-6 p-4 bg-yellow-50 border border-yellow-200 rounded-md">
                <p className="text-sm text-yellow-800">
                  <strong>Tip:</strong> Answer all questions systematically. Time management is key to success in board exams!
                </p>
              </div>
            )}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
